import React, { MouseEvent, useCallback } from 'react';
import { Box, Typography, TypographyProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Tooltip } from '@ui/Tooltip';
import ExpressionTypography from '@features/expressions/ExpressionTypography';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { getActivityTypeLabel } from '@shared/helpers/design/activities.helpers';
import { toPercent } from '@shared/helpers/string.helpers';
import { ActivityColorDot } from '../activities/ActivityColorDot';
import { HoverDeleteButton } from '../components/HoverDeleteButton';
import { clampActivityRefShare } from '@shared/helpers/design/activity-ref.helpers';
import { activityRefGuards } from '@shared/helpers/design/guards/activity-guards.helpers';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { ActivityReference, Storey } from '@shared/models/design/storey.model';
import { UnitNumberInput } from '@ui/UnitNumberInput';
import { clamp, roundToDecimals } from '@shared/helpers/math.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  getBuildingDesign,
  useUpdateBuildingDesign,
} from '../hooks/design.hooks';
import { isDesignSnapshotSupersededError } from '../helpers/design-snapshot-persist.helpers';
import { getStoreyById } from '@shared/helpers/design/storey.helpers';
import { percentageToFactor } from '@shared/helpers/math.helpers';
import {
  addActivityReferenceByType,
  mutateDesign,
} from '@shared/helpers/design/mutators';
import { useSharedAreaOnStorey } from '@features/design/hooks/activity-mix.hooks';
import { useStoreyGfa } from '@features/design/hooks/storeys.hooks';
import { STOREY_SETTINGS_NUMBER_INPUT_WIDTH } from './storey-settings.constants';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useAddActivityRefMenu } from './activity-reference-bar/AddActivityRefMenuContext';

type ActivityRefRowProps = {
  activityRef: ActivityReference;
  storey: Storey;
  area: number;
  totalArea: number;
};

type ActivityRefSummaryRowProps = {
  label: string;
  area: number;
  totalArea: number;
  variant?: TypographyProps['variant'];
};

/**
 * Render a single row for storey activity references.
 * @param props
 * @returns activity reference row
 */
export const ActivityRefRow: React.FC<ActivityRefRowProps> = ({
  activityRef: activityRef,
  storey,
  area,
  totalArea,
}) => {
  const { classes, cx } = useStyles();
  const [isHovered, beginHover, endHover] = useBooleanState(false);
  const updateBuildingDesign = useUpdateBuildingDesign();
  const isReadonly = useIsReadonly();
  const openActivityRefMenu = useAddActivityRefMenu();
  const type = activityRef.type;
  const label = activityRef.name ?? getActivityTypeLabel(activityRef);
  // Convert area to a share for tooltip formatting and clamping.
  const share = totalArea <= 0 ? 0 : clampActivityRefShare(area / totalArea);
  // Unset activity is calculated and should not be removable
  const isUnset = activityRef.type === ActivityType.Unset;
  const isRemovable =
    !isUnset &&
    !activityRefGuards.is.calculatedAreaActivityType(activityRef.type);
  const storeyGfa = useStoreyGfa(storey);
  const sharedArea = useSharedAreaOnStorey(storey);
  // Since program activities are relative (a share) more than 1 is needed to be editable
  const isDisabled =
    isUnset ||
    storey.activities.filter(activityRefGuards.is.programActivityRef).length <=
      1;
  const isClickable = isUnset && !isReadonly;

  /**
   * Resolve the latest design + storey snapshot for mutations.
   * @returns current design and storey or undefined when missing
   */
  const getCurrentStoreySnapshot = useCallback(() => {
    const currentDesign = getBuildingDesign();
    const currentStorey = getStoreyById(currentDesign, storey.id);
    if (!currentStorey) return undefined;
    return { currentDesign, currentStorey };
  }, [storey.id]);

  /**
   * Updates the activity share for a storey reference.
   * @param activityRefId {@link ActivityReference} id
   * @param percent share in percent (0-100)
   * @returns void promise
   */
  const handleSharePercentChange = useCallback(
    async (activityRefId: ActivityReference['id'], percent: number) => {
      const snapshot = getCurrentStoreySnapshot();
      if (!snapshot) return;
      // Keep the adjusted reference intact while redistributing others.
      const nextShare = percentageToFactor(percent);
      try {
        await updateBuildingDesign((design) =>
          mutateDesign.storey.activityRef.update(
            design,
            snapshot.currentStorey.id,
            { id: activityRefId, share: nextShare },
          ),
        );
      } catch (error: unknown) {
        if (isDesignSnapshotSupersededError(error)) return;
        throw error;
      }
    },
    [getCurrentStoreySnapshot, updateBuildingDesign],
  );

  /**
   * Removes an activity reference from the storey.
   * @param target {@link ActivityReference} to remove
   * @returns void promise
   */
  const handleDeleteActivityRef = useCallback(
    async (target: ActivityReference) => {
      const snapshot = getCurrentStoreySnapshot();
      if (!snapshot) return;
      try {
        await updateBuildingDesign((design) =>
          mutateDesign.storey.activityRef.remove(
            design,
            snapshot.currentStorey.id,
            target.activity_id,
          ),
        );
      } catch (error: unknown) {
        if (isDesignSnapshotSupersededError(error)) return;
        throw error;
      }
    },
    [getCurrentStoreySnapshot, updateBuildingDesign],
  );

  /**
   * Updates the area input and syncs its share percentage.
   * @param value new area value in m²
   * @returns void
   */
  const handleAreaChange = useCallback(
    (value?: number) => {
      // Clamp area to keep share within valid bounds.
      const clampedArea = clamp(
        guards.is.number(value) ? value : 0,
        0,
        sharedArea,
      );
      const shareFactor = sharedArea > 0 ? clampedArea / sharedArea : 0;
      const nextPercent = roundToDecimals(shareFactor * 100);
      handleSharePercentChange(activityRef.id, nextPercent);
    },
    [activityRef.id, handleSharePercentChange, sharedArea],
  );

  const handleRemoveClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (isReadonly) return;
    if (!isRemovable) return;
    event.stopPropagation();
    void handleDeleteActivityRef(activityRef);
  };

  /**
   * Opens the activity menu for unset rows and adds the selected activity.
   * @param event click event from the row
   */
  const handleUnsetRowClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!isUnset) return;
      if (isReadonly) return;
      event.stopPropagation();
      void (async () => {
        const selected = await openActivityRefMenu({
          anchorEl: event.currentTarget,
          storey,
        });
        if (!selected) return;
        const snapshot = getCurrentStoreySnapshot();
        if (!snapshot) return;
        try {
          await updateBuildingDesign((design) =>
            addActivityReferenceByType(
              design,
              selected,
              snapshot.currentStorey.id,
            ),
          );
        } catch (error: unknown) {
          if (isDesignSnapshotSupersededError(error)) return;
          throw error;
        }
      })();
    },
    [
      getCurrentStoreySnapshot,
      isReadonly,
      isUnset,
      openActivityRefMenu,
      storey,
      updateBuildingDesign,
    ],
  );

  return (
    <li
      className={classes.root}
      onMouseEnter={beginHover}
      onMouseLeave={endHover}
    >
      <HoverDeleteButton
        isHovered={isHovered}
        onDelete={handleRemoveClick}
        disabled={!isRemovable || isReadonly}
        aria-label="Remove activity"
      >
        <ActivityColorDot
          type={type}
          onClick={handleUnsetRowClick}
          className={cx(isClickable && classes.clickable)}
        />
      </HoverDeleteButton>
      <Box
        className={classes.content}
        component="div"
        onClick={handleUnsetRowClick}
      >
        <Box className={classes.headingRow}>
          <Typography
            variant="body1"
            className={cx(isClickable && classes.clickable)}
          >
            {label}
          </Typography>
        </Box>
      </Box>
      <div className={classes.actions}>
        <Tooltip title={toPercent(share)}>
          <div className={classes.inputWrapper}>
            <UnitNumberInput
              key={activityRef.id}
              value={Math.round(area)}
              min={0}
              max={Math.round(storeyGfa)}
              unit="m²"
              displayUnit="m²"
              size="small"
              textAlignment="right"
              onChange={handleAreaChange}
              disabled={isDisabled || isReadonly}
              outlineVisibility="hover"
            />
          </div>
        </Tooltip>
      </div>
    </li>
  );
};

/**
 * Render a summarized row for calculated activity references.
 * @param props
 * @returns activity reference summary row
 */
export const ActivityRefSummaryRow: React.FC<ActivityRefSummaryRowProps> = ({
  variant = 'body2',
  label,
  area,
  totalArea,
}) => {
  const { classes } = useStyles();
  const share = totalArea <= 0 ? 0 : clampActivityRefShare(area / totalArea);

  return (
    <li className={classes.root}>
      <div className={classes.groupDot} />
      <div className={classes.content}>
        <div className={classes.headingRow}>
          <Typography variant={variant}>{label}</Typography>
        </div>
      </div>
      <div className={classes.actions}>
        <Tooltip title={toPercent(share)}>
          <ExpressionTypography variant="body2" value={area} unit="m²" />
        </Tooltip>
      </div>
    </li>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: `${theme.spacing(4)} 1fr auto`,
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 0),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  headingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  inputWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: STOREY_SETTINGS_NUMBER_INPUT_WIDTH,
  },
  clickable: {
    cursor: 'pointer',
  },
  groupDot: {
    display: 'inline-flex',
    width: theme.spacing(3),
    height: theme.spacing(3),
    borderRadius: '2px',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[200],
    flexShrink: 0,
    justifySelf: 'center',
  },
}));
